import React, { ReactNode } from 'react'
import {
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  EntryType,
  QuestionSettingCode
} from '../../../data/model/questionnaire'
import {
  checkIfMultipleChoice,
  getQuestionSettings
} from '../../../utils/questionnaireUtils'
import { DeleteQuestionButtonContainer } from '../DeleteQuestionButton'
import { QuestionnaireHeaderLabels } from '../HeaderLabels/QuestionnaireHeaderLabels'
import { useStyles } from '../HeaderLabels/QuestionnaireHeaderLabels.styles'
import { useMarketVisibilityDialog } from '../MarketVisibility/MarketVisibilityDialogProvider'
import MarketVisibilityIndicatorIcon from '../MarketVisibility/MarketVisibilityIndicatorIcon'
import { SurveyQuestionHeaderActionsContainer } from '../SurveyQuestionHeaderActions'
import { WaveVisibilityIndicator } from '../WaveVisibility/WaveVisibilityIndicator'
import { AutoSumMenu } from './AutoSumMenu'
import { CardSelector } from './CardSelector'
import { MatrixScreeningMenu } from './MatrixScreeningMenu'
import { QuestionTimerMenu } from './QuestionTimerMenu'
import SourceMediaButton from './SourceMediaButton'
import useEntrySettingValues from './useEntrySettingValues'

interface Props {
  entry: DraftQuestionnaireEntry
  idContent?: JSX.Element
  /** Additional header chips (for example KPI), not part of the ID/type. */
  chipsContent?: ReactNode
  logicContent?: JSX.Element
  kpiContent?: JSX.Element
  titleContent?: JSX.Element
  disabledDuplicate?: boolean
  disableDisplayLogic?: boolean
  onClickDuplicateIcon?: () => void
  onClickAddDisplayLogic?: () => void
  onClickAddLoopSpecificDisplayLogic?: () => void
  onEmptyQuestionCard?: () => void
}

const SurveyQuestionHeaderBase: React.FC<Props> = (props: Props) => {
  const {
    entry,
    idContent,
    chipsContent,
    logicContent,
    kpiContent,
    titleContent,
    disabledDuplicate = false,
    disableDisplayLogic = false,
    onClickDuplicateIcon,
    onClickAddDisplayLogic,
    onClickAddLoopSpecificDisplayLogic,
    onEmptyQuestionCard
  }: Props = props
  const { classes } = useStyles()
  const marketVisibility = useMarketVisibilityDialog()

  const hasQuestionMedia = !!(entry.entryItem as DraftQuestionItem)
    .questionMedia

  const settingValues = useEntrySettingValues(entry.number)

  const questionSettings = getQuestionSettings(settingValues)
  const hasQuestionTimer = questionSettings.has(
    QuestionSettingCode.QuestionTimer
  )
  const hasAutoSum = questionSettings.has(QuestionSettingCode.AutoSumTarget)
  const isMultiSelect = checkIfMultipleChoice(settingValues)

  return (
    <div className="fd-grid fd-container">
      <div className={classes.header}>
        <QuestionnaireHeaderLabels identity={idContent}>
          <WaveVisibilityIndicator entryItem={entry.entryItem} />
          {chipsContent}
        </QuestionnaireHeaderLabels>
        <div
          className={classes.actions}
          role="group"
          aria-label="Question actions"
        >
          {marketVisibility?.hasMarkets && (
            <MarketVisibilityIndicatorIcon
              variant="adornment"
              hiddenInMarkets={marketVisibility.hiddenInMarkets}
              onEdit={marketVisibility.openMarketVisibility}
            />
          )}
          <SourceMediaButton entry={entry} />
          {entry.entryType === EntryType.MatrixEntryType ? (
            <MatrixScreeningMenu matrixTitleLk={entry.entryId} />
          ) : null}
          {hasAutoSum && <AutoSumMenu entry={entry} />}
          {hasQuestionTimer && <QuestionTimerMenu entry={entry} />}
          <div>
            <CardSelector entryId={entry.id} />
          </div>
          <div>
            <DeleteQuestionButtonContainer
              entryType={entry.entryType}
              entryId={entry.id}
            />
          </div>
          <div>
            <SurveyQuestionHeaderActionsContainer
              disableDisplayLogic={disableDisplayLogic}
              hasQuestionMedia={hasQuestionMedia}
              hasQuestionTimer={hasQuestionTimer}
              hasAutoSum={hasAutoSum}
              isMultiSelect={isMultiSelect}
              onClickDuplicateQuestion={onClickDuplicateIcon}
              onClickAddDisplayLogic={onClickAddDisplayLogic}
              onClickAddLoopSpecificDisplayLogic={
                onClickAddLoopSpecificDisplayLogic
              }
              onEmptyQuestionCard={onEmptyQuestionCard}
              disabledDuplicate={disabledDuplicate}
              entry={entry}
            />
          </div>
        </div>
      </div>
      {logicContent}
      {kpiContent}
      {titleContent}
    </div>
  )
}

export default SurveyQuestionHeaderBase
